import { useQuery } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import type { Post } from '../types/calendar';
import { useBrands } from './useBrands';
import { logger } from '../utils/logger';

const API_BASE_URL = 'https://api.socialness.ai';

const convertToISOString = (dateValue: string | Date | undefined): string => {
  if (!dateValue) return '';
  
  if (typeof dateValue === 'string') {
    return dateValue;
  }
  
  if (dateValue instanceof Date) {
    return dateValue.toISOString();
  }
  
  return '';
};

const normalizePost = (post: any): Post => {
  // Handle media conversion - now media is an array
  let media = null;
  
  if (post.media) {
    if (typeof post.media === 'string') {
      // Simple string URL (legacy support)
      media = { url: post.media, type: 'image' as const };
    } else if (Array.isArray(post.media) && post.media.length > 0) {
      // New array format - take the first media item
      const firstMedia = post.media[0];
      
      // Handle Firestore mapValue format
      if (firstMedia?.mapValue?.fields) {
        const fields = firstMedia.mapValue.fields;
        media = {
          url: fields.url?.stringValue || '',
          type: (fields.type?.stringValue?.toLowerCase() === 'video' ? 'video' : 'image') as 'image' | 'video',
          thumbnailUrl: fields.thumbnailUrl?.stringValue
        };
      }
      // Handle standard object format
      else if (firstMedia && firstMedia.url) {
        const mediaTypeFromPost = firstMedia.type === 'video' ? 'video' : 'image';
        media = {
          url: firstMedia.url,
          type: mediaTypeFromPost as 'image' | 'video',
          thumbnailUrl: firstMedia.thumbnailUrl
        };
      }
      // Handle string format in array
      else if (typeof firstMedia === 'string') {
        media = {
          url: firstMedia,
          type: 'image' as const
        };
      }
    } else if (post.media.url) {
      // Legacy object format (backward compatibility)
      const mediaTypeFromPost = post.media.type === 'video' ? 'video' : 'image';
      media = {
        url: post.media.url,
        type: mediaTypeFromPost as 'image' | 'video',
        thumbnailUrl: post.media.thumbnailUrl
      };
    }
  }
  
  return {
    id: post.id,
    content: post.content || '',
    status: post.status || 'draft',
    brandId: post.brandId || '',
    brandName: post.brandName || '',
    brandLogo: post.brandLogo || '',
    platforms: post.platforms || [],
    scheduledAt: convertToISOString(post.scheduledAt),
    createdAt: convertToISOString(post.createdAt),
    updatedAt: convertToISOString(post.updatedAt),
    media,
    format: post.format || 'post',
    reviewerId: post.reviewerId,
    userId: post.userId
  };
};

export const usePosts = () => {
  const { data: brandsData } = useBrands();
  
  return useQuery({
    queryKey: ['posts'],
    queryFn: async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('No authenticated user');
        }

        const token = await user.getIdToken();
        const response = await fetch(`${API_BASE_URL}/management/posts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Normalize all posts
        const normalizedPosts = (data.posts || []).map(normalizePost);
        
        return {
          posts: normalizedPosts,
          pagination: data.pagination || {},
          source: 'api'
        };
      } catch (error) {
        logger.error('usePosts', 'Failed to fetch posts', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};